import { TableColumn } from "./index";
import { useRef, useEffect } from "react";

export function useSticky<T>(columns: readonly TableColumn<T>[]) {
  const columnRefs = useRef<(HTMLTableCellElement | null)[]>([]);

  // Initialize refs array
  useEffect(() => {
    columnRefs.current = columnRefs.current.slice(0, columns.length);
  }, [columns.length]);

  const getStickyOffset = (column: TableColumn<T>, index: number) => {
    if (!column.sticky) return 0;

    if (column.sticky === "right") {
      // For right sticky columns, measure the widths of all columns to the right
      const stickyColumns = columns.slice(index + 1).filter((col) => col.sticky === "right");
      return stickyColumns.reduce((offset, col) => {
        const colIndex = columns.indexOf(col);
        const colRef = columnRefs.current[colIndex];
        return offset + (colRef?.offsetWidth || col.width || 0);
      }, 0);
    } else {
      // For left sticky columns, measure the widths of all columns to the left
      const stickyColumns = columns.slice(0, index).filter((col) => col.sticky === "left");
      return stickyColumns.reduce((offset, col) => {
        const colIndex = columns.indexOf(col);
        const colRef = columnRefs.current[colIndex];
        return offset + (colRef?.offsetWidth || col.width || 0);
      }, 0);
    }
  };

  const getStickyStyles = (column: TableColumn<T>, index: number, isStickyHeader?: boolean, isStickyCell?: boolean) => {
    const isSticky = column.sticky || isStickyCell;
    if (!isSticky && !isStickyHeader) return {};

    const offset = getStickyOffset(column, index);
    const position = column.sticky === "right" ? "right" : "left";

    return {
      position: "sticky",
      top: isStickyHeader ? 0 : "none",
      [position]: offset,
      backgroundColor: "background.paper",
      zIndex: isStickyHeader && isSticky ? 1200 : isSticky ? 1000 : 900,

      "&::after": isSticky
        ? {
            content: '""',
            position: "absolute",
            [position === "right" ? "left" : "right"]: 0,
            top: 0,
            bottom: 0,
            width: "1px",
            backgroundColor: "divider"
          }
        : {},

      "&::before": isStickyHeader
        ? {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "1px",
            backgroundColor: "divider",
            zIndex: 1201
          }
        : {}
    };
  };

  return {
    getStickyStyles,
    columnRefs
  };
}
